import { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { request } from "../../config/request.ts";
import { formatTime } from "../../config/formatters.ts";
import dayjs from "dayjs";
import "../../styles/scss/PublicDashboard.scss";

interface Wash {
  id: number;
  vehicleArrivedDate?: string;
  vehicleArrivedTime?: string;
  washingCompletedTime?: string;
  washStatus?: string;
  paymentStatus?: string;
  actualAmount?: number | string;
  payedAmount?: number | string;
  remarks?: string;
  vehicle?: { regNo?: string; vehicleDetails?: string } | null;
  customer?: { name?: string; phone?: string } | null;
  masterServices?: { id: number; serviceName: string }[];
}

interface DashboardData {
  counts: { pending: number; completed: number; paymentPending: number };
  pendingWashes: Wash[];
  completedWashes: Wash[];
  paymentPendingWashes: Wash[];
}

interface ColumnConfig {
  key: keyof Pick<
    DashboardData,
    "pendingWashes" | "completedWashes" | "paymentPendingWashes"
  >;
  countKey: keyof DashboardData["counts"];
  title: string;
  icon: string;
  accent: string;
  soft: string;
}

const COLUMNS: ColumnConfig[] = [
  {
    key: "pendingWashes",
    countKey: "pending",
    title: "Pending Washes",
    icon: "hourglass_top",
    accent: "#d97706",
    soft: "rgba(245, 158, 11, 0.14)",
  },
  {
    key: "paymentPendingWashes",
    countKey: "paymentPending",
    title: "Payment Pending",
    icon: "payments",
    accent: "#2563eb",
    soft: "rgba(37, 99, 235, 0.14)",
  },
  {
    key: "completedWashes",
    countKey: "completed",
    title: "Completed Washes",
    icon: "check_circle",
    accent: "#059669",
    soft: "rgba(16, 185, 129, 0.14)",
  },
];

const statusScheme = (status?: string, type: "wash" | "payment" = "wash") => {
  const base =
    type === "payment"
      ? { icon: "payments", label: "Payment" }
      : { icon: "local_car_wash", label: "Wash" };

  switch (status) {
    case "Completed":
      return { ...base, color: "#059669", soft: "rgba(16, 185, 129, 0.14)" };
    case "Partial":
      return { ...base, color: "#2563eb", soft: "rgba(37, 99, 235, 0.14)" };
    case "Pending":
    default:
      return { ...base, color: "#d97706", soft: "rgba(245, 158, 11, 0.14)" };
  }
};

const formatAmount = (value?: number | string) =>
  value != null && value !== "" ? `₹${Number(value).toLocaleString()}` : "-";

export default function PublicDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [range, setRange] = useState<"today" | "yesterday">("today");
  const [mobileTab, setMobileTab] = useState<ColumnConfig["key"]>(
    "pendingWashes",
  );
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [clock, setClock] = useState(dayjs());

  useEffect(() => {
    const timer = setInterval(() => setClock(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  const load = useCallback(
    async (silent = false) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(false);
      try {
        const params: Record<string, any> = { limit: 100 };
        const date =
          range === "today"
            ? dayjs().format("YYYY-MM-DD")
            : dayjs().subtract(1, "day").format("YYYY-MM-DD");
        params.fromDate = date;
        params.toDate = date;
        const res = await request.get("/api/publicDashboard", params);
        setData(res?.data?.data || null);
        setLastUpdated(new Date());
      } catch {
        setError(true);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [range],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => load(true), 30000);
    return () => clearInterval(timer);
  }, [load]);

  const renderWash = (wash: Wash, column: ColumnConfig) => {
    const services = wash?.masterServices || [];
    const isPayment = column.key === "paymentPendingWashes";
    const isCompleted = column.key === "completedWashes";

    // Pending & Completed columns show the payment status,
    // the Payment Pending column shows the wash status.
    const badgeStatus = isPayment ? wash?.washStatus : wash?.paymentStatus;
    const scheme = statusScheme(
      badgeStatus,
      isPayment ? "wash" : "payment",
    );

    return (
      <div className="pd-wash" key={wash.id}>
        <div className="pd-wash-row">
          <div style={{ minWidth: 0 }}>
            <div className="pd-reg">
              {wash?.vehicle?.regNo || (isPayment ? "No Vehicle" : "-")}
            </div>
            {wash?.vehicle?.vehicleDetails && (
              <div className="pd-vehicle-details">
                {wash.vehicle.vehicleDetails}
              </div>
            )}
          </div>
          <span
            className="pd-badge"
            style={
              {
                "--accent": scheme.color,
                "--soft": scheme.soft,
              } as React.CSSProperties
            }
          >
            <span className="material-symbols-outlined">{scheme.icon}</span>
            {scheme.label} {badgeStatus || "Pending"}
          </span>
        </div>

        <div className="pd-wash-meta">
          <span className="pd-meta-item">
            <span className="material-symbols-outlined">person</span>
            {wash?.customer?.name || "-"}
          </span>
          <span className="pd-meta-item">
            <span className="material-symbols-outlined">schedule</span>
            {formatTime(
              isCompleted
                ? wash?.washingCompletedTime
                : wash?.vehicleArrivedTime,
            )}
          </span>
          {isPayment && (
            <span className="pd-meta-item" style={{ color: "#2563eb" }}>
              <span className="material-symbols-outlined">currency_rupee</span>
              {formatAmount(wash?.actualAmount)}
            </span>
          )}
          {isCompleted && wash?.payedAmount != null && (
            <span className="pd-meta-item" style={{ color: "#059669" }}>
              <span className="material-symbols-outlined">currency_rupee</span>
              {formatAmount(wash?.payedAmount)}
            </span>
          )}
        </div>

        {services.length > 0 && (
          <div className="pd-services">
            {services.map((service) => (
              <span className="pd-service-chip" key={service.id}>
                {service.serviceName}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="public-dashboard">
      <header className="pd-header">
        <div className="pd-header-inner">
          <div className="pd-brand">
            <div className="pd-brand-icon">
              <span className="material-symbols-outlined">local_car_wash</span>
            </div>
            <div className="pd-brand-text">
              <div className="pd-title">Live Wash Status</div>
              <div className="pd-subtitle">
                <span className="pd-live-dot" />
                Updated {clock.format("hh:mm:ss A")}
              </div>
            </div>
          </div>

          <div className="pd-header-actions">
            <div className="pd-segmented">
              <button
                className={range === "today" ? "active" : ""}
                onClick={() => setRange("today")}
              >
                Today
              </button>
              <button
                className={range === "yesterday" ? "active" : ""}
                onClick={() => setRange("yesterday")}
              >
                Yesterday
              </button>
            </div>
            <button
              className={`pd-refresh ${refreshing ? "spinning" : ""}`}
              onClick={() => load(true)}
              disabled={refreshing}
            >
              <span className="material-symbols-outlined">refresh</span>
              <span className="pd-refresh-label">Refresh</span>
            </button>
          </div>
        </div>
      </header>

      <div className="pd-content">
        {loading && !data ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
            <CircularProgress sx={{ color: "#b71c1c" }} />
          </div>
        ) : (
          <>
            {/* <div className="pd-stats">
              {COLUMNS.map((column, index) => (
                <div
                  className="pd-stat"
                  key={column.key}
                  style={
                    {
                      "--accent": column.accent,
                      "--soft": column.soft,
                      animationDelay: `${index * 0.06}s`,
                    } as React.CSSProperties
                  }
                >
                  <div className="pd-stat-top">
                    <div className="pd-stat-label">
                      {column.title.replace(" Washes", "")}
                    </div>
                    <div className="pd-stat-icon">
                      <span className="material-symbols-outlined">
                        {column.icon}
                      </span>
                    </div>
                  </div>
                  <div className="pd-stat-value">
                    {data?.counts?.[column.countKey] ??
                      data?.[column.key]?.length ??
                      0}
                  </div>
                </div>
              ))}
            </div> */}

            {error && (
              <div
                style={{
                  textAlign: "center",
                  color: "#dc2626",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  marginBottom: 16,
                }}
              >
                Unable to load the latest status. Retrying soon…
              </div>
            )}

            <div className="pd-mobile-tabs">
              {COLUMNS.map((column) => (
                <button
                  key={column.key}
                  className={`pd-mobile-tab ${
                    mobileTab === column.key ? "active" : ""
                  }`}
                  style={
                    {
                      "--accent": column.accent,
                      "--soft": column.soft,
                    } as React.CSSProperties
                  }
                  onClick={() => setMobileTab(column.key)}
                >
                  <span className="material-symbols-outlined">
                    {column.icon}
                  </span>
                  <span className="pd-mobile-tab-label">
                    {column.title.replace(" Washes", "")}
                  </span>
                  <span className="pd-mobile-tab-count">
                    {data?.counts?.[column.countKey] ??
                      data?.[column.key]?.length ??
                      0}
                  </span>
                </button>
              ))}
            </div>

            <div className="pd-columns">
              {COLUMNS.map((column) => {
                const washes = data?.[column.key] || [];
                return (
                  <section
                    className={`pd-column ${
                      mobileTab === column.key ? "active" : ""
                    }`}
                    key={column.key}
                  >
                    <div
                      className="pd-column-head"
                      style={
                        {
                          "--accent": column.accent,
                          "--soft": column.soft,
                        } as React.CSSProperties
                      }
                    >
                      <div className="pd-column-icon">
                        <span className="material-symbols-outlined">
                          {column.icon}
                        </span>
                      </div>
                      <div className="pd-column-title">{column.title}</div>
                      <div className="pd-column-count">{washes.length}</div>
                    </div>

                    <div className="pd-list">
                      {washes.length === 0 ? (
                        <div className="pd-empty">
                          <span className="material-symbols-outlined">
                            {column.key === "completedWashes"
                              ? "task_alt"
                              : "inbox"}
                          </span>
                          <p>No {column.title.toLowerCase()}</p>
                        </div>
                      ) : (
                        washes.map((wash) => renderWash(wash, column))
                      )}
                    </div>
                  </section>
                );
              })}
            </div>

            {lastUpdated && (
              <div className="pd-footer">
                Last updated at {dayjs(lastUpdated).format("DD MMM YYYY, hh:mm:ss A")} 
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
